import { CircleDollarSign, Users } from 'lucide-react';
import { formatRoundDate } from '@nemo/core/model';
import { Link } from 'wouter';
import { DockedPane, PaneBack, PaneClose } from '../pane/DockedPane.js';
import { hostOf } from '../pane/fields.js';
import { PaneLinks, type OutsideLink } from '../pane/PaneLinks.js';
import { formatDay } from '../ui/format.js';
import { SectionIcon } from '../ui/SectionIcon.js';
import { SiteIcon, siteOf } from '../ui/SiteIcon.js';
import { ROUND_SOURCE_LABELS, formatRoundAmount, formatRoundLabel } from './format.js';
import { PartnerLink, attributedPartner } from './PartnerLink.js';
import type { Round, RoundParticipant } from './types.js';

/**
 * One round, in the docked pane every record opens in: the company and where
 * to read about it, what was raised, and who was in it. Each investor we hold
 * is a way into that fund's pane, and a partner named on the round a way to
 * them, which a row of chips painted on a canvas cannot be.
 */
export function RoundPane(props: {
  round: Round;
  onClose: () => void;
  onStep: (delta: 1 | -1) => void;
}): React.JSX.Element {
  const { round } = props;
  const outside: OutsideLink[] = [];
  if (round.companyWebsite !== null) {
    outside.push({
      label: 'Website',
      href: round.companyWebsite,
      title: hostOf(round.companyWebsite),
      aria: 'on the web',
    });
  }
  if (round.newsUrl !== null) {
    outside.push({
      label: 'Press',
      href: round.newsUrl,
      title: round.newsUrl,
      aria: 'in the press',
    });
  }
  const date = formatRoundDate(round);

  return (
    <DockedPane label={round.companyName} onClose={props.onClose} onStep={props.onStep}>
      <header className="pane-head">
        <PaneBack onClose={props.onClose} />
        <div className="pane-head-line">
          <SiteIcon site={siteOf(round.companyWebsite)} name={round.companyName} size={28} />
          <strong className="pane-name" role="heading" aria-level={2}>
            {round.companyName}
          </strong>
          <span className="pane-head-actions">
            <PaneClose onClose={props.onClose} />
          </span>
        </div>
        <PaneLinks name={round.companyName} links={outside} />
        {round.tldr !== null && <p className="subtle">{round.tldr}</p>}
      </header>

      <section className="pane-section">
        <h3 className="pane-section-title">
          <SectionIcon icon={CircleDollarSign} />
          Round
        </h3>
        <dl className="pane-facts">
          <Fact label="Round" value={formatRoundLabel(round.roundLabel)} />
          <Fact label="Amount" value={formatRoundAmount(round.amountEur)} />
          <Fact label="Date" value={date} />
          <Fact label="Sector" value={round.sector} />
          <Fact label="Country" value={round.hqCountries.join(', ')} />
          <Fact label="Source" value={ROUND_SOURCE_LABELS[round.source]} />
          <Fact label="First seen" value={formatDay(round.firstSeenAt)} />
        </dl>
      </section>

      <section className="pane-section">
        <h3 className="pane-section-title">
          <SectionIcon icon={Users} />
          Investors
        </h3>
        {round.participants.length === 0 ? (
          <p className="subtle">The round names no investors.</p>
        ) : (
          <div className="pane-list">
            {round.participants.map((participant, index) => (
              <Investor
                key={`${participant.fundId ?? participant.nameRaw}-${index}`}
                participant={participant}
              />
            ))}
          </div>
        )}
      </section>
    </DockedPane>
  );
}

function Fact(props: { label: string; value: string | null }): React.JSX.Element | null {
  if (props.value === null || props.value === '') return null;
  return (
    <div className="pane-fact">
      <dt className="pane-fact-label">{props.label}</dt>
      <dd className="pane-fact-value">{props.value}</dd>
    </div>
  );
}

/**
 * A name the feed matched to a fund we hold opens that fund; one it did not is
 * plain. Who at the fund did the round follows: "Accel (Sonali De Rycker)".
 */
function Investor({ participant }: { participant: RoundParticipant }): React.JSX.Element {
  const label = participant.fundName ?? participant.nameRaw;
  const partner = attributedPartner(participant);
  return (
    <p className="round-line">
      {participant.fundId === null ? (
        <span className="round-company subtle">{label}</span>
      ) : (
        <Link href={`/?fund=${participant.fundId}`} className="round-company">
          {label}
        </Link>
      )}
      {participant.lead && <span className="tag">Lead</span>}
      {partner !== null && (
        <span className="participant-partner">
          (<PartnerLink partner={partner} />)
        </span>
      )}
    </p>
  );
}
